/* eslint-disable react/require-default-props -- defaults via destructuring; no defaultProps (React 19) */
import React from 'react';
import PropTypes from 'prop-types';
import './Alert.scss';
import Button from '../Button';
import { FlexColumn, FlexRow } from '../GridsAndLayouts/GridsAndLayouts';
import { Typography } from '../Typography';
import { CloseFilledIcon, CloseIcon } from '../Icons/CloseIcon';
import { AlertFilledIcon } from '../Icons/AlertIcon';
import { CheckCircleFilledIcon } from '../Icons/CheckCircleIcon';
import { InfoIcon } from '../Icons/InfoIcon';

/** Values from `foundations/variables.scss` — string form so `FlexRow` / `FlexColumn` emit `var(--ds-space-*)`. */
const [DS_SPACE_1, DS_SPACE_2, DS_SPACE_3] = ['var(--ds-space-1)', 'var(--ds-space-2)', 'var(--ds-space-3)'];

/** Matches `--ds-space-5` in `foundations/variables.scss` (icon / hit-target artboard). */
const DEFAULT_ALERT_ICON_SIZE = 20;

const NEUTRAL_DEFAULT_ICON_FILL = 'var(--pal-grey-slate-1000, #32343F)';

function renderDefaultIcon(variant) {
  switch (variant) {
    case 'success':
      return <CheckCircleFilledIcon size={DEFAULT_ALERT_ICON_SIZE} />;
    case 'error':
      return <CloseFilledIcon size={DEFAULT_ALERT_ICON_SIZE} strokeWidth={1.5} />;
    case 'warning':
      return <AlertFilledIcon size={DEFAULT_ALERT_ICON_SIZE} />;
    case 'neutral':
      return <AlertFilledIcon size={DEFAULT_ALERT_ICON_SIZE} strokeWidth={2} fill={NEUTRAL_DEFAULT_ICON_FILL} />;
    case 'info':
    default:
      return <InfoIcon size={DEFAULT_ALERT_ICON_SIZE} fill="var(--pal-blue-1000)" />;
  }
}

function inferLayout({ layout, dismissible, description, list, actions, link, variant }) {
  const hasList = Array.isArray(list) && list.length > 0;
  if (layout) return layout;
  if (dismissible && !description && !hasList && !actions?.length && !link) {
    return 'dismiss-inline';
  }
  if (link && !description && !hasList && !actions?.length) {
    if (variant === 'info') return 'link-right';
    if (variant === 'neutral') return 'inline-link';
  }
  return 'stack';
}

function iconSizeClass(layout, variant) {
  if (layout === 'dismiss-inline' || layout === 'link-right') return 'ds-alert__icon-slot--sm';
  if (layout === 'inline-link') return 'ds-alert__icon-slot--md';
  if (variant === 'success' && layout === 'stack') return 'ds-alert__icon-slot--sm';
  return 'ds-alert__icon-slot--md';
}

function AlertIconSlot({ layout, variant, children }) {
  return (
    <FlexRow
      className={`ds-alert__icon-slot ${iconSizeClass(layout, variant)}`}
      align="start"
      justify="center"
      fullWidth={false}
    >
      {children}
    </FlexRow>
  );
}

const Alert = ({
  variant = 'info',
  layout: layoutProp,
  title = '',
  titleId,
  description = '',
  list,
  actions,
  link,
  icon,
  showDefaultIcon = false,
  accent = false,
  dismissible = false,
  onDismiss,
  className = '',
}) => {
  const layout = inferLayout({
    layout: layoutProp,
    dismissible,
    description,
    list,
    actions,
    link,
    variant,
  });

  const hasIconSlot = Boolean(icon) || showDefaultIcon;
  let iconEl = null;
  if (icon != null) iconEl = icon;
  else if (showDefaultIcon) iconEl = renderDefaultIcon(variant);

  const classes = [
    'ds-alert',
    `ds-alert--${variant}`,
    `ds-alert--layout-${layout}`,
    accent && 'ds-alert--accent',
    hasIconSlot && 'ds-alert--has-icon',
    dismissible && layout === 'stack' && 'ds-alert--has-dismiss-stack',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const hasList = Array.isArray(list) && list.length > 0;
  const hasTextBlock = Boolean(title || description || hasList);

  const textStack = hasTextBlock ? (
    <FlexColumn className="ds-alert__text-stack" align="start" justify="start" gap={DS_SPACE_2} fullWidth={false}>
      {title ? (
        <Typography variant="t15" id={titleId} className="ds-alert__title">
          {title}
        </Typography>
      ) : null}
      {description ? (
        <Typography variant="t17" className="ds-alert__description">
          {description}
        </Typography>
      ) : null}
      {hasList ? (
        <ul className="ds-alert__list">
          {list.map((item) => (
            <li key={item}>
              <Typography variant="t17" className="ds-alert__list-item">
                {item}
              </Typography>
            </li>
          ))}
        </ul>
      ) : null}
    </FlexColumn>
  ) : null;

  const actionsRow =
    actions && actions.length > 0 ? (
      <FlexRow className="ds-alert__actions" align="start" justify="start" gap={15} fullWidth={false}>
        {actions.map((action) => (
          <Button
            key={action.label}
            variant="secondary"
            size="sm"
            type="button"
            onClick={action.onClick}
            className="ds-alert__action-btn"
          >
            <Typography variant="t15">{action.label}</Typography>
          </Button>
        ))}
      </FlexRow>
    ) : null;

  const renderLinkLabel = (labelVariant) =>
    link ? (
      <FlexRow
        inline
        className="ds-alert__text-link-inner"
        align="center"
        justify="start"
        gap={DS_SPACE_1}
        fullWidth={false}
      >
        <Typography variant={labelVariant}>{link.label}</Typography>
        {link.icon ? (
          <FlexRow
            inline
            className="ds-alert__text-link-icon"
            align="center"
            justify="center"
            fullWidth={false}
            style={{ lineHeight: 0 }}
          >
            {link.icon}
          </FlexRow>
        ) : null}
      </FlexRow>
    ) : null;

  const dismissBtn = dismissible ? (
    <Button
      variant="tertiary"
      size="sm"
      type="button"
      className="ds-alert__dismiss"
      onClick={onDismiss || (() => {})}
      aria-label="Dismiss alert"
      icon={<CloseIcon size={DEFAULT_ALERT_ICON_SIZE} strokeWidth={1.5} />}
    />
  ) : null;

  /* ——— link-right (info): icon | [ title …………… link ] ——— */
  if (layout === 'link-right') {
    return (
      <FlexRow className={classes} role="alert" align="center" justify="start" gap={DS_SPACE_3} fullWidth={false}>
        {hasIconSlot ? (
          <AlertIconSlot layout={layout} variant={variant}>
            {iconEl}
          </AlertIconSlot>
        ) : null}
        <FlexRow className="ds-alert__link-row" align="center" justify="between" gap={DS_SPACE_3} fullWidth={false}>
          {title ? (
            <Typography variant="t15" className="ds-alert__title ds-alert__title--single">
              {title}
            </Typography>
          ) : (
            <FlexRow className="ds-alert__title-spacer" fullWidth={false} aria-hidden />
          )}
          {link ? (
            <Button variant="link" size="sm" type="button" className="ds-alert__text-link" onClick={link.onClick}>
              {renderLinkLabel('t15')}
            </Button>
          ) : null}
        </FlexRow>
      </FlexRow>
    );
  }

  /* ——— dismiss-inline: icon | title …………… [dismiss] ——— */
  if (layout === 'dismiss-inline') {
    return (
      <FlexRow className={classes} role="alert" align="center" justify="start" gap={DS_SPACE_3} fullWidth={false}>
        {hasIconSlot ? (
          <AlertIconSlot layout={layout} variant={variant}>
            {iconEl}
          </AlertIconSlot>
        ) : null}
        <FlexRow className="ds-alert__dismiss-row" align="center" justify="start" gap={DS_SPACE_3} fullWidth={false}>
          {title ? (
            <Typography variant="t15" className="ds-alert__title ds-alert__title--grow">
              {title}
            </Typography>
          ) : null}
          {dismissBtn}
        </FlexRow>
      </FlexRow>
    );
  }

  /* ——— inline-link (neutral): icon | title text — link (gap 4) ——— */
  if (layout === 'inline-link') {
    return (
      <FlexRow className={classes} role="alert" align="start" justify="start" gap={DS_SPACE_3} fullWidth={false}>
        {hasIconSlot ? (
          <AlertIconSlot layout={layout} variant={variant}>
            {iconEl}
          </AlertIconSlot>
        ) : null}
        <FlexRow
          className="ds-alert__inline-link-row"
          align="center"
          justify="start"
          gap={DS_SPACE_1}
          fullWidth={false}
        >
          {title ? (
            <Typography variant="t15" className="ds-alert__title ds-alert__title--inline">
              {title}
            </Typography>
          ) : null}
          {link ? (
            <Button variant="link" size="sm" type="button" className="ds-alert__text-link" onClick={link.onClick}>
              {renderLinkLabel('t17')}
            </Button>
          ) : null}
        </FlexRow>
      </FlexRow>
    );
  }

  /* ——— stack (default): icon | column [ textStack gap15 actions ] + optional link + dismiss absolute ——— */
  let bodyGap = 0;
  if (textStack && actionsRow) bodyGap = 15;
  else if (textStack && link && layout === 'stack' && !actionsRow) bodyGap = DS_SPACE_3;
  else if (!textStack && actionsRow) bodyGap = 0;

  const bodyColumn = (
    <FlexColumn className="ds-alert__body" align="start" justify="start" gap={bodyGap} fullWidth={false}>
      {textStack}
      {actionsRow}
      {link && layout === 'stack' ? (
        <Button variant="link" size="sm" type="button" className="ds-alert__stack-link" onClick={link.onClick}>
          {renderLinkLabel('buttonMedium')}
        </Button>
      ) : null}
    </FlexColumn>
  );

  return (
    <FlexRow className={classes} role="alert" align="start" justify="start" gap={DS_SPACE_3} fullWidth={false}>
      {hasIconSlot ? (
        <AlertIconSlot layout={layout} variant={variant}>
          {iconEl}
        </AlertIconSlot>
      ) : null}
      {bodyColumn}
      {dismissible && layout === 'stack' ? dismissBtn : null}
    </FlexRow>
  );
};

Alert.propTypes = {
  variant: PropTypes.oneOf(['success', 'error', 'warning', 'info', 'neutral']),
  layout: PropTypes.oneOf(['stack', 'link-right', 'dismiss-inline', 'inline-link']),
  title: PropTypes.string,
  titleId: PropTypes.string,
  description: PropTypes.string,
  list: PropTypes.arrayOf(PropTypes.string),
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      onClick: PropTypes.func,
    })
  ),
  link: PropTypes.shape({
    label: PropTypes.string,
    onClick: PropTypes.func,
    icon: PropTypes.node,
  }),
  icon: PropTypes.node,
  showDefaultIcon: PropTypes.bool,
  accent: PropTypes.bool,
  dismissible: PropTypes.bool,
  onDismiss: PropTypes.func,
  className: PropTypes.string,
};

export default Alert;
